import type { LeadKind } from "pages/StartLead/flow";

/** What the lead flow hands the sign-in screen through router state. The
 * grant is an opaque bearer token: it travels in state only, never in a
 * URL, and is never logged. */
export type LoginGrantState = {
  loginGrant: string;
  /** What the lead was for, when the screen handing over the grant knows. */
  leadKind?: LeadKind;
  /** The method the visitor chose before arriving, when they chose one. */
  method?: "sms_otp" | "email_link";
  /** The quote an account is being made from, when the grant is for that:
   * the sign-in screen can ask for a replacement grant for it. */
  quoteId?: string;
};

const GRANT_TOKEN = /^[0-9a-f]{64}$/;
const QUOTE_ID = /^[A-Za-z0-9_-]{1,64}$/;

function isLeadKind(value: unknown): value is LeadKind {
  return value === "death" || value === "imminent" || value === "planning";
}

/** The login-grant inputs in a location's state, or null when the screen
 * is not in grant mode. A malformed token is not grant mode: the screen
 * then behaves exactly as it does without the field. */
export function readLoginGrantState(state: unknown): LoginGrantState | null {
  if (typeof state !== "object" || state === null) return null;
  const record = state as Record<string, unknown>;
  const { loginGrant, leadKind, method, quoteId } = record;
  if (typeof loginGrant !== "string" || !GRANT_TOKEN.test(loginGrant)) {
    return null;
  }
  return {
    loginGrant,
    ...(isLeadKind(leadKind) ? { leadKind } : {}),
    ...(method === "sms_otp" || method === "email_link" ? { method } : {}),
    ...(typeof quoteId === "string" && QUOTE_ID.test(quoteId)
      ? { quoteId }
      : {}),
  };
}
